import { Link } from 'react-router-dom'

// The one button on the website. Every page composes this; no page defines its
// own again. (The old site hand rolled a gradient CTA in six files.)
//
// ⛔ CONTRAST, and why `fill` is #1C7A9C and not the apps' #218FB7:
// the apps fill with #218FB7 because an app button’s label is 17pt semibold, and
// white on #218FB7 is 3.69:1, which clears AA only as LARGE text. A 16px web
// button is normal text and needs 4.5:1. White on #1C7A9C is 4.87:1. #218FB7
// stays the hover state and the colour of every link and icon on the site.
// Do not "tidy" this back to one value. docs/MARKETING-SITE-SPEC.md §2.1.
//
// Variants:
//   fill    the one real call to action on a section. Dark teal, white label.
//   outline the second action beside a fill. Ink hairline, ink label.
//   light   a fill on a NAVY or photographic section: white plate, ink label.
//   quiet   a text action. Underline on hover, no chrome.
//   quietLight  the same text action on navy or over a photograph.
//
// ⛔ FULL PILL, every size. Both apps moved every button to a capsule
// (founder 2026-10-02, "buttons FULL PILL app-wide"), so the website does too.
// A 14px corner beside the apps' pills read as a different product.

const BASE =
  'inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap ' +
  'transition-[background-color,border-color,color,transform] duration-150 ' +
  'active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none'

const SIZES = {
  lg: 'text-[17px] px-7 py-4 rounded-full',
  md: 'text-[15px] px-5 py-3 rounded-full',
  sm: 'text-[14px] px-4 py-2.5 rounded-full',
}

const VARIANTS = {
  fill:    'bg-action-fill text-white hover:bg-action',
  outline: 'border border-ink/15 text-ink hover:border-ink/35 hover:bg-ink/[0.03]',
  light:   'bg-white text-ink hover:bg-white/90',
  quiet:   'text-link hover:underline underline-offset-4 px-0 py-0',
  // A text action on navy or over photography. Its own variant, not
  // `quiet` plus className="text-white": two colour utilities on one element
  // resolve by stylesheet order, so the override was a coin toss.
  quietLight: 'text-white hover:underline underline-offset-4 px-0 py-0',
}

export default function Button({
  as, to, href, variant = 'fill', size = 'lg', className = '', children, ...rest
}) {
  const cls = `${BASE} ${variant === 'quiet' || variant === 'quietLight' ? '' : SIZES[size]} ${VARIANTS[variant]} ${className}`
  if (to)   return <Link to={to} className={cls} {...rest}>{children}</Link>
  if (href) return <a href={href} className={cls} {...rest}>{children}</a>
  const Tag = as ?? 'button'
  return <Tag className={cls} {...rest}>{children}</Tag>
}
